import type { GenerationResult, GeneratorOptions, Hero, PfpProject } from "./types";
import { MAX_TOKEN_ATTEMPTS, comboKey, mulberry32 } from "./internals";
import { selectOnce } from "./selection";

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type = "image/png"): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), type);
  });
}

function substitute(
  template: string,
  ctx: { tokenId: number; project: PfpProject; selection: Record<string, string> },
): string {
  return template
    .replace(/\{id\}/g, String(ctx.tokenId))
    .replace(/\{collection\}/g, ctx.project.name)
    .replace(/\{trait_([a-zA-Z0-9_-]+)\}/g, (_, layerId: string) => {
      const layer = ctx.project.layers.find((l) => l.id === layerId);
      if (!layer) return "";
      const traitId = ctx.selection[layerId];
      const trait = layer.traits.find((t) => t.id === traitId);
      return trait?.name ?? "";
    });
}

/**
 * Render the full mint batch: roll every token (with dedup + heroes),
 * compose the layered canvas at canvasSize, build the metadata JSON
 * (image: "" — caller fills after pinning) and return one
 * GenerationResult per token.
 */
export async function generateBatch(
  project: PfpProject,
  opts: GeneratorOptions,
): Promise<GenerationResult[]> {
  if (project.layers.length === 0) throw new Error("project has no layers");
  const rand = opts.seed !== undefined ? mulberry32(opts.seed) : Math.random;

  // Preload trait images.
  const imgCache = new Map<string, HTMLImageElement>();
  for (const l of project.layers) {
    for (const t of l.traits) {
      if (!imgCache.has(t.imageUrl)) imgCache.set(t.imageUrl, await loadImage(t.imageUrl));
    }
  }

  const heroByToken = new Map<number, Hero>();
  for (const h of project.heroes) heroByToken.set(h.tokenId, h);

  const used = new Set<string>();
  const results: GenerationResult[] = [];

  for (let id = 1; id <= opts.count; id++) {
    let selection: Record<string, string> | null = null;
    let attempts = 0;
    while (attempts < MAX_TOKEN_ATTEMPTS) {
      attempts++;
      const candidate = selectOnce(project, rand, heroByToken.get(id));
      if (!candidate) continue;
      const key = comboKey(candidate);
      if (heroByToken.has(id)) {
        // Heroes are unique by definition; record the key but skip the dedup gate.
        used.add(key);
        selection = candidate;
        break;
      }
      if (used.has(key)) continue;
      used.add(key);
      selection = candidate;
      break;
    }
    if (!selection) {
      throw new Error(
        `could not generate token ${id} after ${MAX_TOKEN_ATTEMPTS} attempts — rules may be too strict, or N exceeds unique combos`,
      );
    }

    const canvas = document.createElement("canvas");
    canvas.width = project.canvasSize.width;
    canvas.height = project.canvasSize.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");

    const layersOrdered = [...project.layers].sort((a, b) => a.order - b.order);
    const attrs: { trait_type: string; value: string }[] = [];
    for (const layer of layersOrdered) {
      const traitId = selection[layer.id];
      if (!traitId) continue;
      const trait = layer.traits.find((t) => t.id === traitId);
      if (!trait) continue;
      const img = imgCache.get(trait.imageUrl)!;
      ctx.drawImage(img, 0, 0, project.canvasSize.width, project.canvasSize.height);
      attrs.push({ trait_type: layer.name, value: trait.name });
    }

    const blob = await canvasToBlob(canvas);
    const subCtx = { tokenId: id, project, selection };
    const name = substitute(project.metadata.namePattern || `${project.name} #{id}`, subCtx);
    const description = project.metadata.descriptionTemplate
      ? substitute(project.metadata.descriptionTemplate, subCtx)
      : "";

    results.push({
      tokenId: id,
      attributes: attrs,
      selection,
      blob,
      metadata: {
        name,
        description,
        image: "",
        ...(project.metadata.externalUrl ? { external_url: project.metadata.externalUrl } : {}),
        attributes: attrs,
      },
    });

    opts.onProgress?.(id, opts.count);
  }

  return results;
}

/**
 * Roll one token + render its composited PNG, returned as a data URL.
 * Cheap to call on every keystroke when memoized at the React layer.
 *
 * `targetSize` lets the editor render previews at smaller resolution
 * than the project's canvasSize — keeps the editor's memory footprint
 * sane (12-tile gallery + heroes + roll preview can otherwise pin many
 * MB of base64 PNGs). Final batch generation always uses canvasSize via
 * generateBatch.
 */
export async function samplePreview(
  project: PfpProject,
  opts?: { hero?: Hero; seed?: number; targetSize?: number },
): Promise<{ selection: Record<string, string>; dataUrl: string } | null> {
  if (project.layers.length === 0) return null;
  const rand = opts?.seed !== undefined ? mulberry32(opts.seed) : Math.random;

  let selection: Record<string, string> | null = null;
  for (let attempt = 0; attempt < MAX_TOKEN_ATTEMPTS && !selection; attempt++) {
    selection = selectOnce(project, rand, opts?.hero);
  }
  if (!selection) return null;

  const layersOrdered = [...project.layers].sort((a, b) => a.order - b.order);
  const size = opts?.targetSize ?? project.canvasSize.width;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  for (const layer of layersOrdered) {
    const traitId = selection[layer.id];
    if (!traitId) continue;
    const trait = layer.traits.find((t) => t.id === traitId);
    if (!trait) continue;
    try {
      const img = await loadImage(trait.imageUrl);
      ctx.drawImage(img, 0, 0, size, size);
    } catch {
      // Skip layers with broken images instead of aborting the whole preview.
    }
  }

  return { selection, dataUrl: canvas.toDataURL("image/png") };
}
